import { Box, Stack, type StackProps, Typography, styled } from "@mui/material"
import type { ReactNode } from "react"
import { ChevronRight } from "react-feather"
import type { SourceName } from "../../../types"
import { isDefined } from "../../../utils"
import { DiscountChip, EvolutionStatChip } from "../../dataDisplay"
import { CardBottomColorLayout, type CardBottomColorLayoutProps } from "../Card"
import { renderTooltip } from "./SalesCardTooltip"

export type SalesCardProps = Omit<
  CardBottomColorLayoutProps,
  "title" | "children"
> & {
  title: ReactNode
  children?: ReactNode
  period?: string | null
  actionLabel?: string
  sources?: SourceName[]
  tooltipMsg?: string
}

const LINK_TO_CLASSNAME = "chevron"
const BOX_CLASSNAME = "box"

export const SalesCardV2 = ({
  children,
  period,
  title,
  tooltip,
  onClick,
  bottomColor,
  actionLabel = "Voir plus",
  sources,
  tooltipMsg,
}: SalesCardProps) => {
  const hasAction = !!onClick

  return (
    <Container>
      <CardBottomColorLayout
        bottomColor={bottomColor}
        onClick={onClick}
        title={title}
        tooltip={
          isDefined(tooltip)
            ? tooltip
            : renderTooltip({ period, sources, tooltipMsg })
        }
      >
        <Stack gap={2} my={0.5}>
          {children}

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ opacity: +hasAction }}
            className={BOX_CLASSNAME}
            style={{ transition: "color ease-in-out 150ms" }}
          >
            <Typography variant="12px">{actionLabel}</Typography>
            <ChevronRight
              size={12}
              className={LINK_TO_CLASSNAME}
              style={{ transition: "all ease-in-out 150ms" }}
            />
          </Stack>
        </Stack>
      </CardBottomColorLayout>
    </Container>
  )
}

export const SalesCardV2StatsAndEvolution = ({
  mainStat,
  evol,
  slotProps,
}: {
  mainStat: string
  evol?: number
  slotProps?: {
    container?: StackProps
  }
}) => (
  <Stack {...slotProps?.container} sx={{ gap: 0.75, ...slotProps?.container?.sx }}>
    <Typography variant="24px" fontWeight={600}>
      {mainStat}
    </Typography>
    <Typography variant="20px">
      <EvolutionStatChip
        evol={evol}
        border={false}
        arrow
        formatOptions={{ signDisplay: "never" }}
      />
    </Typography>
  </Stack>
)

export const SalesCardV2Discount = ({ discount }: { discount: string }) => (
  <Stack gap={0.75}>
    <Typography variant="12px" sx={{ color: (theme) => theme.palette.grey.light }}>
      Remise
    </Typography>
    <DiscountChip label={`${discount} %`} fontSize={21} variant="small" />
  </Stack>
)

const Container = styled(Box)(
  ({
    theme: {
      palette: { branding },
    },
  }) => ({
    width: "100%",
    "&:hover": {
      [`.${LINK_TO_CLASSNAME}`]: {
        transform: "translateX(3px)",
        color: branding.dark2,
      },
      [`.${BOX_CLASSNAME}`]: {
        color: branding.dark2,
      },
    },
    [`.${LINK_TO_CLASSNAME},.${BOX_CLASSNAME}`]: {
      color: branding.light1,
    },
  }),
)
